/** @format */

import axios from "axios";
import type {
  ApiSuccess,
  Collection,
  PublicCollectionWithPhotos,
} from "@outstanding/shared";

/**
 * Collection API service. Only the two functions the app actually calls
 * (Collection.vue / Homepage.vue) — the unused by-id / all / admin variants
 * were dead code and were removed (bead hgd.11), along with the legacy
 * Firebase-era `@/types` shapes they advertised: both callers narrow the
 * payload to their own view-model types, so the payload stays `unknown` here
 * rather than lying about its shape.
 */

/**
 * Get a collection by its slug, or null when it doesn't exist (404).
 * @throws Error for any non-404 API failure
 */
export async function getCollectionBySlug(
  slug: string,
): Promise<PublicCollectionWithPhotos | null> {
  try {
    const response = await axios.get<ApiSuccess<PublicCollectionWithPhotos>>(
      `/api/collections/${slug}`,
    );
    // NULL MEANS 404, AND ONLY 404 — Collection.vue reads it as "no such
    // collection" and routes to NotFound. A 2xx whose envelope carries no
    // payload is a malformed response, so throw and let it be handled as the
    // error it is rather than let it masquerade as a missing collection.
    if (!response.data.data) {
      throw new Error(
        `Malformed collection response for "${slug}": 2xx with no payload`,
      );
    }
    return response.data.data;
  } catch (error) {
    if (axios.isAxiosError(error) && error.response?.status === 404) {
      return null;
    }
    throw error;
  }
}

/**
 * Get the collections to display on the homepage.
 *
 * Opted out of the global error redirect: Homepage.vue catches this one and
 * renders the static carousel with no collection grid, so a 500 here must not
 * eject the visitor to the full-page Error view before that fallback can run
 * (bead outstanding-raq5). getCollectionBySlug above deliberately does NOT opt
 * out — there the collection IS the page, so ejecting is the right answer.
 *
 * @throws Error if the API request fails
 */
export async function getHomepageCollections(): Promise<Collection[]> {
  const response = await axios.get<ApiSuccess<Collection[]>>(
    "/api/collections/homepage",
    { skipGlobalErrorRedirect: true },
  );
  return response.data.data ?? [];
}
